import { useMemo, useCallback, useState } from "react";
import styleAMM from "../../../../pages/AMM/AMM.module.scss";
import assetStyle from "./Asset.module.scss";
import { addressFormate, amountValueFormate, equalsIgnoreCase, formatNumber, roundTo } from "../../../../../_utils/comman";
import { ImageWithFallback } from "../../../";
import { AssetType, Reputation } from "../../../../../_utils/types";
import Notable from "../../../../../assets/images/notable.svg";
import Verified from "../../../../../assets/images/verified.svg";
import Destroyed from "../../../../../assets/images/destroyed-asset.svg";
import Question from "../../../../../assets/images/question-mark.svg";
import { CustomTooltip } from "../../../CustomTooltip/CustomTooltip";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../services/hooks.service";
import classNames from "classnames";
import { getLogo } from "../../../../../wallets/providers";
import BigNumber from "bignumber.js";
import { useEVMBalance } from "../../../../../hooks/useEVMBalance";
import { useVisibility } from "../../../../../hooks/useVisability";

interface Props {
  asset: AssetType;
  handleAssetSelect?: (asset: AssetType) => void;
  showReputation?: boolean;
}

export const Asset = ({ asset, handleAssetSelect, showReputation }: Props) => {
  const { t } = useTranslation();
  const evmBalance = useAppSelector((state) => state.user.userBalances[`${asset.index}_${asset.chainId}`]);
  const [isVisible, setIsVisible] = useState(false);

  const handleVisibilityChange = useCallback((visible: boolean) => {
    setIsVisible(visible);
  }, []);

  const [elementRef] = useVisibility(handleVisibilityChange);

  useEVMBalance(asset, isVisible);

  const assetName = asset.name || "";
  const assetnUnitName = asset.unit_name || "";

  const usdValues = useAppSelector((state) => state.exchange.dollarPrices);
  const userAssets = useAppSelector((state) => state.wallet.userAssets);

  const assetAmount = () => {
    if (asset) {
      if (evmBalance) {
        return amountValueFormate(evmBalance.balance, asset.decimal);
      }
      const userAsset = userAssets.find((a) => equalsIgnoreCase(a.index, asset.index));
      if (userAsset) {
        return amountValueFormate(userAsset.amount, asset.decimal);
      } else {
        return amountValueFormate(asset.amount, asset.decimal) || "0";
      }
    }
    return "0";
  };

  const displayAssetIndex = (index) => {
    if (index?.toString().length > 10) {
      return addressFormate(index, 5);
    }
    return index ?? '';
  };

  const handleSelect = () => {
    if (handleAssetSelect) {
      handleAssetSelect(asset);
    }
  };

  const totalValue = useMemo(() => {
    if (asset) {
      const usdValue = usdValues[asset.id];
      if (usdValue) {
        return roundTo(BigNumber(assetAmount()).multipliedBy(usdValue).toString(), 2);
      }
      return '0.00';
    }
    return '0.00';
  }, [asset, usdValues]);

  const reputationIcon = (reputation) => {
    if (reputation === Reputation.Verified) {
      return (
        <CustomTooltip placement="left" text={getTooltipDescription(reputation)} hidden={!showReputation}>
          <img src={Verified} alt="check icon" className={assetStyle.asset_check} />
        </CustomTooltip>
      );
    }
    if (reputation === Reputation.Notable) {
      return (
        <CustomTooltip placement="left"  text={getTooltipDescription(reputation)} hidden={!showReputation}>
          <img src={Notable} alt="check icon" className={assetStyle.asset_check} />
        </CustomTooltip>
      );
    }
    if (reputation === Reputation.Scam || reputation === Reputation.Destroyed || reputation === Reputation.Unsafe) {
      return (
        <CustomTooltip placement="left" text={getTooltipDescription(reputation)} hidden={!showReputation}>
          <img src={Destroyed} alt="check icon" className={assetStyle.asset_check} />
        </CustomTooltip>
      );
    }
    return <></>;
  };

  const getTooltipDescription = (reputation): string => {
    switch (reputation) {
      case Reputation.Verified:
        return t("asset_tooltip.verified_token");
      case Reputation.Notable:
        return t("asset_tooltip.notable_token");
      case Reputation.Neutral:
        return t("asset_tooltip.neutral_token");
      case Reputation.Unverified:
        return t("asset_tooltip.unverified_token");
      case Reputation.Deprecated:
        return t("asset_tooltip.deprecated_token");
      case Reputation.Unsafe:
        return t("asset_tooltip.unsafe_token");
      case Reputation.Scam:
        return t("asset_tooltip.scam_token");
      case Reputation.Destroyed:
        return t("asset_tooltip.destroyed_token");
      default:
        return "";
    }
  };

  return (
    <div
      ref={elementRef}
      onClick={handleSelect}
      className={classNames(assetStyle.asset, {
        [assetStyle.selectable]: handleAssetSelect,
        [assetStyle.transparent]: asset.reputation === Reputation.Unverified,
      })}
    >
      <div className={assetStyle.asset_info}>
        <CustomTooltip placement="top" text={getTooltipDescription(asset.reputation)} hidden={!showReputation}>
          <div>
            <ImageWithFallback src={asset?.img} fallbackSrc={Question} alt="asset icon" className={assetStyle.asset_img} />
          </div>
        </CustomTooltip>
        <div className={assetStyle.asset_name}>
          <div>
            <span>{assetnUnitName}</span>
            {showReputation
              ? reputationIcon(asset.reputation)
              : asset.chainId && <img className={assetStyle.asset_chain} src={getLogo(asset.chainId)} alt="icon" />}
          </div>
          <p className={styleAMM.asset_txt_gray}>{assetName + " " + displayAssetIndex(asset.index)}</p>
        </div>
      </div>
      <div className={assetStyle.asset_info}>
        <div className={assetStyle.asset_balance}>
          <p data-testid={`${assetnUnitName}_balance`}>{roundTo(assetAmount(), 6)}</p>
          <p className={styleAMM.asset_txt_gray}>${formatNumber(totalValue)}</p>
        </div>
      </div>
    </div>
  );
};
